import { useCallback, useMemo } from 'react'
import {
  type Blocker,
  type Location,
  useBeforeUnload,
  useBlocker,
} from 'react-router-dom'

export interface UseUnsavedChangesOptions {
  shouldBlock?: (args: {
    currentLocation: Location
    nextLocation: Location
  }) => boolean
}

export interface UseUnsavedChangesReturn {
  isBlocked: boolean
  proceed: () => void
  reset: () => void
  blocker: Blocker
}

export function useUnsavedChanges(
  isDirty: boolean,
  options?: UseUnsavedChangesOptions,
): UseUnsavedChangesReturn {
  const shouldBlockFn = options?.shouldBlock

  const blocker = useBlocker(
    useCallback(
      ({
        currentLocation,
        nextLocation,
      }: {
        currentLocation: Location
        nextLocation: Location
      }): boolean => {
        if (!isDirty) return false
        if (shouldBlockFn) {
          return shouldBlockFn({ currentLocation, nextLocation })
        }
        return currentLocation.pathname !== nextLocation.pathname
      },
      [isDirty, shouldBlockFn],
    ),
  )

  useBeforeUnload(
    useCallback(
      (event: BeforeUnloadEvent): void => {
        if (isDirty) {
          event.preventDefault()
          event.returnValue = ''
        }
      },
      [isDirty],
    ),
  )

  const proceed = useCallback((): void => {
    if (blocker.state === 'blocked') {
      blocker.proceed()
    }
  }, [blocker])

  const reset = useCallback((): void => {
    if (blocker.state === 'blocked') {
      blocker.reset()
    }
  }, [blocker])

  return useMemo(
    () => ({
      isBlocked: blocker.state === 'blocked',
      proceed,
      reset,
      blocker,
    }),
    [blocker, proceed, reset],
  )
}
